/**
 * `/jobs/new` — the form, on a page of its own.
 *
 * Six buttons across the record pages send somebody here: the primary action
 * on a property and a contact, the New button on each of their Jobs tabs, and
 * the green button on a phone. Until now none of them worked: nothing was
 * routed at `/jobs/new`, so `/jobs/:id` matched with `id = "new"`, asked the
 * server for a job whose id is not a uuid, and showed "Not found · Invalid
 * uuid" — on the most obvious button on two of the most-used pages.
 *
 * It is a route rather than a modal opened from each page because a link is a
 * thing somebody can send, bookmark and come back to, and because the same
 * form is already reachable from the Jobs board's New button. `<JobForm>` is
 * that form; this is the wrapper that says where the job came from and where
 * to go next.
 */
import { useNavigate, useSearchParams } from 'react-router-dom'
import { JobForm } from '../components/forms/job-form.js'
import { EmptyState } from '../components/primitives.js'

export function NewJob({ canCreate }: { canCreate: boolean }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()

  /*
   * A property is one answer; a contact is not. `?property=` names the site
   * the job is at, so the form opens with it chosen. `?contact=` says who
   * asked, and a contact can be linked to six properties — there is no single
   * site to fill in, so the form opens on its property picker instead of
   * guessing one and being wrong five times in six.
   */
  const propertyId = params.get('property')

  /* Where Cancel goes: back where they came from, or the board. */
  const leave = () => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/jobs')
  }

  if (!canCreate) {
    return (
      <EmptyState
        title="Not yours to add"
        body="Creating a job needs edit access to Jobs. Somebody with it can add one for you."
        action={{ label: 'Back to jobs', onClick: () => navigate('/jobs') }}
      />
    )
  }

  return (
    <>
      {/*
        * Something behind the modal, so the page is not a white sheet while
        * the form is open and is not an empty one if it is dismissed.
        */}
      <EmptyState
        title="New job"
        body="A job is a piece of work at a property. Give it a title and a site; the quote, the visits and the invoice all hang off it."
      />

      <JobForm
        open
        onClose={leave}
        {...(propertyId ? { propertyId } : {})}
        onSaved={(id) => navigate(`/jobs/${id}`, { replace: true })}
        onSavedAndQuote={(jobId, quoteId) => navigate(
          quoteId ? `/quotes/${quoteId}` : `/jobs/${jobId}`, { replace: true })}
      />
    </>
  )
}
